import { createFileRoute } from "@tanstack/react-router";
import { useServerFn } from "@tanstack/react-start";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { useMemo, useState } from "react";
import { Search, Loader2 } from "lucide-react";
import { Input } from "@/components/ui/input";
import { MemberAvatar } from "@/components/dashboard/MemberAvatar";
import { useAuth } from "@/lib/auth-context";
import { listAdminSubscribers, setSubscriberAccess, type AdminSubscriber } from "@/lib/admin-overview.functions";

export const Route = createFileRoute("/_authenticated/admin/subscribers")({
  head: () => ({ meta: [{ title: "Subscribers | Admin | XAU Pro Academy" }, { name: "robots", content: "noindex" }] }),
  component: SubscribersPage,
});

type Filter = "all" | "paid" | "comp" | "free" | "telegram";
const TIERS = ["free", "standard", "pro", "elite"] as const;
const isPaid = (s: AdminSubscriber) => s.plan !== "free" && !s.complimentary && !s.isAdmin;
const isComp = (s: AdminSubscriber) => !!s.complimentary || (s.isAdmin && s.plan !== "free");

function initials(s: AdminSubscriber) {
  const base = s.name || s.email || "?";
  return base.split(/[\s@.]+/).filter(Boolean).slice(0, 2).map((w) => w[0]!.toUpperCase()).join("");
}

function ago(iso: string | null) {
  if (!iso) return "Never";
  const m = Math.floor((Date.now() - new Date(iso).getTime()) / 60000);
  if (m < 60) return `${Math.max(m, 1)}m ago`;
  const h = Math.floor(m / 60);
  if (h < 48) return `${h}h ago`;
  return `${Math.floor(h / 24)}d ago`;
}

function SubscribersPage() {
  const { user, isHydrated } = useAuth();
  const fetchList = useServerFn(listAdminSubscribers);
  const q = useQuery({ queryKey: ["admin", "subscribers", user?.id], queryFn: () => fetchList(), enabled: isHydrated && !!user });
  const qc = useQueryClient();
  const setAccess = useServerFn(setSubscriberAccess);
  const mut = useMutation({
    mutationFn: (v: { userId: string; tier: string; reason: string }) => setAccess({ data: v }),
    onSuccess: (r) => { toast.success(`Access set to ${r.tier}`); qc.invalidateQueries({ queryKey: ["admin", "subscribers"] }); },
    onError: (e: Error) => toast.error(e.message || "Could not update access"),
  });
  const [term, setTerm] = useState("");
  const [filter, setFilter] = useState<Filter>("all");

  const rows = useMemo(() => {
    const t = term.trim().toLowerCase();
    return (q.data ?? []).filter((s) => {
      if (t && !`${s.name ?? ""} ${s.email ?? ""} ${s.telegram?.username ?? ""}`.toLowerCase().includes(t)) return false;
      if (filter === "paid") return isPaid(s);
      if (filter === "comp") return isComp(s);
      if (filter === "free") return s.plan === "free";
      if (filter === "telegram") return !!s.telegram?.active;
      return true;
    });
  }, [q.data, term, filter]);

  const all = q.data ?? [];
  const totals = {
    all: all.length,
    paid: all.filter(isPaid).length,
    comp: all.filter(isComp).length,
    free: all.filter((s) => s.plan === "free").length,
    telegram: all.filter((s) => s.telegram?.active).length,
    delivered: all.reduce((n, s) => n + s.delivered, 0),
  };

  return (
    <div className="space-y-6">
      <div>
        <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-gold">Members ledger</p>
        <h1 className="mt-1 font-display text-3xl font-bold">Subscribers</h1>
        <p className="mt-1 text-sm text-muted-foreground">Every account, its plan and Telegram alert delivery.</p>
      </div>

      <div className="grid gap-3 sm:grid-cols-3 lg:grid-cols-6">
        {([["Accounts", totals.all], ["Paid", totals.paid], ["Complimentary", totals.comp], ["Free", totals.free], ["Telegram linked", totals.telegram], ["Alerts delivered", totals.delivered]] as const).map(([k, v]) => (
          <div key={k} className="orion-surface rounded-xl p-4">
            <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-muted-foreground">{k}</p>
            <p className="mt-1 font-mono text-2xl font-semibold tabular-nums">{q.data ? v : "\u2013"}</p>
          </div>
        ))}
      </div>

      <div className="flex flex-wrap items-center gap-3">
        <div className="relative w-full max-w-xs">
          <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
          <Input value={term} onChange={(e) => setTerm(e.target.value)} placeholder="Search name, email, Telegram" className="pl-9" />
        </div>
        <div className="flex gap-1 rounded-lg bg-foreground/5 p-1">
          {(["all", "paid", "comp", "free", "telegram"] as Filter[]).map((f) => (
            <button
              key={f}
              onClick={() => setFilter(f)}
              className={`rounded-md px-3 py-1 font-mono text-[10px] uppercase tracking-wider transition-colors ${filter === f ? "bg-gold-muted text-gold" : "text-muted-foreground hover:text-foreground"}`}
            >
              {f === "comp" ? "complimentary" : f}
            </button>
          ))}
        </div>
      </div>

      <div className="orion-surface overflow-x-auto rounded-xl">
        {q.isLoading ? (
          <div className="flex items-center justify-center p-12"><Loader2 className="h-6 w-6 animate-spin text-gold" /></div>
        ) : q.isError ? (
          <p className="p-8 text-center text-sm text-destructive">Could not load subscribers.</p>
        ) : (
          <table className="w-full min-w-[860px] text-sm">
            <thead>
              <tr className="border-b border-border/40 text-left font-mono text-[10px] uppercase tracking-[0.15em] text-muted-foreground">
                <th className="px-4 py-3 font-normal">Member</th>
                <th className="px-4 py-3 font-normal">Plan</th>
                <th className="px-4 py-3 font-normal">Login</th>
                <th className="px-4 py-3 font-normal">Telegram</th>
                <th className="px-4 py-3 text-right font-normal">Alerts delivered</th>
                <th className="px-4 py-3 font-normal">Joined</th>
                <th className="whitespace-nowrap px-4 py-3 font-normal">Last sign in</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((s) => (
                <tr key={s.id} className="border-b border-border/20 transition-colors last:border-0 hover:bg-foreground/[0.03]">
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-3">
                      <MemberAvatar src={s.avatarUrl} initials={initials(s)} name={s.name ?? s.email ?? "Member"} className="h-8 w-8 rounded-full bg-gold/15 text-[11px] text-gold" />
                      <div className="min-w-0">
                        <p className="truncate font-medium">{s.name ?? "Unnamed"}</p>
                        <p className="truncate text-xs text-muted-foreground">{s.email}</p>
                      </div>
                    </div>
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex flex-wrap items-center gap-1">
                      {s.isAdmin ? (
                        <span className={`rounded-full px-2 py-0.5 font-mono text-[10px] uppercase tracking-wider ${s.plan === "free" ? "bg-foreground/5 text-muted-foreground" : "bg-gold-muted text-gold"}`}>{s.plan}</span>
                      ) : (
                        <select
                          aria-label={`Plan for ${s.name ?? s.email ?? "member"}`}
                          value={s.plan}
                          disabled={mut.isPending || (s.plan !== "free" && !s.complimentary)}
                          title={s.plan !== "free" && !s.complimentary ? "Billed subscription. Manage through billing." : "Complimentary access"}
                          onChange={(e) => mut.mutate({ userId: s.id, tier: e.target.value, reason: s.complimentary?.reason ?? "test" })}
                          className="rounded-md border border-border/50 bg-background px-2 py-1 font-mono text-[10px] uppercase tracking-wider text-foreground disabled:opacity-60"
                        >
                          {TIERS.map((t) => <option key={t} value={t}>{t}</option>)}
                        </select>
                      )}
                      {s.complimentary && !s.isAdmin && (
                        <select
                          aria-label="Grant reason"
                          value={s.complimentary.reason}
                          disabled={mut.isPending}
                          onChange={(e) => mut.mutate({ userId: s.id, tier: s.plan, reason: e.target.value })}
                          className="rounded-md border border-border/50 bg-background px-1.5 py-1 font-mono text-[10px] uppercase tracking-wider text-muted-foreground"
                        >
                          {["test", "partner", "staff"].map((r) => <option key={r} value={r}>{r}</option>)}
                        </select>
                      )}
                      {isComp(s) && <span className="rounded-full bg-foreground/10 px-2 py-0.5 font-mono text-[10px] uppercase tracking-wider text-muted-foreground">Comp</span>}
                      {s.isAdmin && <span className="rounded-full bg-foreground/10 px-2 py-0.5 font-mono text-[10px] uppercase tracking-wider">Admin</span>}
                    </div>
                  </td>
                  <td className="px-4 py-3 text-xs capitalize text-muted-foreground">{s.provider ?? "email"}</td>
                  <td className="px-4 py-3 text-xs">
                    {s.telegram ? (
                      <span className="inline-flex items-center gap-2">
                        <span className={`h-1.5 w-1.5 rounded-full ${s.telegram.active ? "bg-emerald" : "bg-muted-foreground/40"}`} />
                        {s.telegram.username ? `@${s.telegram.username}` : s.telegram.active ? "Linked" : "Pending"}
                      </span>
                    ) : (
                      <span className="text-muted-foreground">Not linked</span>
                    )}
                  </td>
                  <td className="px-4 py-3 text-right font-mono tabular-nums">
                    {s.delivered}
                    {s.failed > 0 && <span className="ml-1 text-xs text-destructive">({s.failed} failed)</span>}
                  </td>
                  <td className="px-4 py-3 whitespace-nowrap font-mono text-xs tabular-nums text-muted-foreground">{s.createdAt.slice(0, 10)}</td>
                  <td className="px-4 py-3 text-xs text-muted-foreground">{ago(s.lastSignInAt)}</td>
                </tr>
              ))}
              {rows.length === 0 && (
                <tr><td colSpan={7} className="p-8 text-center text-sm text-muted-foreground">No members match these filters.</td></tr>
              )}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}
